/**
 * CommentReply - Individual reply display within a comment thread
 */
import React from "react"
import { Button } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/free-solid-svg-icons"
import { CommentReply } from "types/comments.types"
import { formatRelativeTime } from "utils/relativeTime"

interface CommentReplyProps {
  reply: CommentReply
  onDelete?: () => void
}

/**
 * Parse backend date format (yyyy-mm-dd hh:mm:ss) to Date object
 * Also handles ISO 8601 format for backwards compatibility
 * Returns current date if dateString is undefined (e.g., during Yjs sync before backend response)
 */
const parseDate = (dateString: string | undefined): Date => {
  if (!dateString) return new Date()
  // Backend format: "2025-12-01 14:30:45" - replace space with T for ISO 8601
  const normalizedDate = dateString.includes("T")
    ? dateString
    : dateString.replace(" ", "T")
  return new Date(normalizedDate)
}

export const CommentReplyItem: React.FC<CommentReplyProps> = ({
  reply,
  onDelete,
}) => {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete?.()
  }

  return (
    <div className="bg-white rounded border border-gray-200 p-2">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          {/* Author avatar placeholder */}
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
            style={{ backgroundColor: "#8B5CF6" }}
          >
            {reply.authorName.charAt(0).toUpperCase()}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-xs text-gray-900">
              {reply.authorName}
            </span>
            <span className="text-xs text-gray-400">
              {formatRelativeTime(parseDate(reply.createdAt))}
            </span>
          </div>
        </div>

        {/* Delete button */}
        {onDelete && (
          <Button
            type="text"
            danger
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={handleDelete}
            title="Delete reply"
            aria-label="Delete reply"
          />
        )}
      </div>

      {/* Reply content */}
      <div className="text-xs text-gray-700 whitespace-pre-wrap break-words pl-8">
        {reply.content}
      </div>
    </div>
  )
}

export default CommentReplyItem
